import React from 'react';
import { Plus, FileText, ChevronLeft, ChevronRight } from 'lucide-react';
import { PlanCard } from './PlanCard';
import { PropertiesPanel } from './PropertiesPanel';

export const LeftPanel = ({
    isOpen, setIsOpen, canManageProject, handleAddFloor, floorPlans, loading,
    handlePlanDragStart, handlePlanDragEnd, handlePlanDragOver, handleDragLeaveContainer, handlePlanDrop, handleCardDrop,
    draggedPlanId, dropIndicator, currentPlanStates, activePlanId, editingPlanId, handleDoubleClick,
    handleSetActive, tempName, setTempName, handleNameChange, handleKeyDown, triggerFileUpload, handleDeleteRequest,
    selectedEntityId, onSelectEntity: parentOnSelectEntity, uploadProgress, expandedPlanId, handleToggleExpand, mobileView, setMobileView,
    draggedOverPlanId, handleDragEnterCard, handleDragLeaveCard, isAreaComplete
}) => {
    return (
        <>
            <aside 
                className={`transition-transform duration-300 ease-in-out bg-white dark:bg-gray-800 border-r dark:border-gray-700 flex flex-col w-72 absolute top-0 left-0 h-full z-40 shadow-lg ${isOpen ? 'translate-x-0' : '-translate-x-full'}`}
            >
                <div className="flex justify-between items-center flex-shrink-0 p-4">
                    <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200 truncate">Floor Plans</h2>
                    {canManageProject && <button onClick={handleAddFloor} className="p-1 rounded-md text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                        <Plus className="h-5 w-5" />
                    </button>}
                </div>

                <div 
                    className="flex-grow overflow-y-auto space-y-2 p-4 pt-0"
                    onDragLeave={handleDragLeaveContainer}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={handlePlanDrop}
                >
                    <div className="space-y-2 pr-1">
                        {floorPlans.map(plan => (
                            <div key={plan.id}
                                className={`relative transition-transform duration-300 ease-in-out ${draggedPlanId === plan.id ? 'opacity-30' : 'opacity-100'}`}
                            >
                                {dropIndicator?.targetId === plan.id && dropIndicator.position === 'before' && (
                                    <div className="absolute -top-1 left-0 right-0 h-1.5 bg-blue-500 rounded-full z-20 pointer-events-none"></div>
                                )}
                                <div
                                    draggable={canManageProject}
                                    onDragStart={e => handlePlanDragStart(e, plan.id)}
                                    onDragEnd={handlePlanDragEnd}
                                    onDragOver={e => handlePlanDragOver(e, plan.id)}
                                    onDrop={e => handleCardDrop(e, plan.id)}
                                >
                                    <PlanCard 
                                        plan={plan} 
                                        planEntities={currentPlanStates?.[plan.id] || []}
                                        onSelectEntity={(entityId) => {
                                            parentOnSelectEntity(entityId, plan.id);
                                            if (mobileView !== 'canvas') setMobileView('canvas');
                                        }}
                                        activePlanId={activePlanId}
                                        editingPlanId={editingPlanId}
                                        handleDoubleClick={handleDoubleClick}
                                        handleSetActive={handleSetActive}
                                        tempName={tempName}
                                        setTempName={setTempName}
                                        handleNameChange={handleNameChange}
                                        handleKeyDown={handleKeyDown}
                                        canManageProject={canManageProject}
                                        triggerFileUpload={triggerFileUpload}
                                        handleDeleteRequest={handleDeleteRequest}
                                        selectedEntityId={selectedEntityId}
                                        uploadProgress={uploadProgress}
                                        isExpanded={expandedPlanId === plan.id}
                                        onToggleExpand={() => handleToggleExpand(plan.id)}
                                        draggedOverPlanId={draggedOverPlanId}
                                        handleDragEnterCard={handleDragEnterCard}
                                        handleDragLeaveCard={handleDragLeaveCard}
                                        isAreaComplete={isAreaComplete}
                                    />
                                </div>
                                {dropIndicator?.targetId === plan.id && dropIndicator.position === 'after' && (
                                    <div className="absolute -bottom-1 left-0 right-0 h-1.5 bg-blue-500 rounded-full z-20 pointer-events-none"></div>
                                )}
                            </div>
                        ))}
                        {loading && (
                            <p className="text-sm text-center text-gray-500 dark:text-gray-400 py-10">Loading plans...</p>
                        )}
                        {!loading && floorPlans.length === 0 && (
                            <div className="text-center text-gray-500 dark:text-gray-400 py-10">
                                <FileText className="h-8 w-8 mx-auto" />
                                <p className="mt-2 text-sm">No floor plans yet.</p>
                                {canManageProject && <p className="text-xs mt-1">Click '+' to add one.</p>}
                            </div>
                        )}
                    </div>
                </div>
            </aside>
            <button 
                onClick={() => setIsOpen(!isOpen)} 
                className={`absolute top-1/2 -translate-y-1/2 z-50 px-0.5 py-2 bg-white dark:bg-gray-700 rounded-r-lg shadow hover:bg-gray-50 dark:hover:bg-gray-600 transition-all duration-300 hidden md:block ${isOpen ? 'left-72' : 'left-0'}`}
            >
                {isOpen ? <ChevronLeft className="h-3 w-3 text-gray-600 dark:text-gray-300" /> : <ChevronRight className="h-3 w-3 text-gray-600 dark:text-gray-300" />}
            </button>
        </>
    );
};

export const RightPanel = ({
    isOpen, setIsOpen, canManageProperties, selectedEntity, selectedEntityIds, onPropertyChange, onDelete, canEdit, isDark, handleRestoreAspectRatio, materials, furniture, decorativeImages, openLibrary, onSelectEdge, selectedEdgeIndices, onClearEdgeSelection, onDeleteCustomEdge, entities, onAddFurnitureClick, onAddFurnitureOption, onSelectEntity, onEditItem
}) => {
    return (
        <>
            {canManageProperties && 
                <>
                    <aside className={`transition-transform duration-300 ease-in-out bg-white dark:bg-gray-800 border-l dark:border-gray-700 flex flex-col w-72 absolute top-0 right-0 h-full z-40 shadow-lg ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}>
                        <div className="flex-grow p-4 overflow-y-auto">
                            <PropertiesPanel
                                selectedEntity={selectedEntity}
                                selectedEntityIds={selectedEntityIds}
                                onPropertyChange={onPropertyChange}
                                onDelete={onDelete}
                                canManage={canEdit}
                                isDark={isDark}
                                handleRestoreAspectRatio={handleRestoreAspectRatio}
                                materials={materials}
                                furniture={furniture}
                                decorativeImages={decorativeImages}
                                openLibrary={openLibrary}
                                onSelectEdge={onSelectEdge}
                                selectedEdgeIndices={selectedEdgeIndices}
                                onClearEdgeSelection={onClearEdgeSelection}
                                onDeleteCustomEdge={onDeleteCustomEdge}
                                entities={entities}
                                onAddFurnitureClick={onAddFurnitureClick}
                                onAddFurnitureOption={onAddFurnitureOption}
                                onSelectEntity={onSelectEntity}
                                onEditItem={onEditItem}
                            />
                        </div>
                    </aside>
                    <button 
                        onClick={() => setIsOpen(!isOpen)} 
                        className={`absolute top-1/2 -translate-y-1/2 z-50 px-0.5 py-2 bg-white dark:bg-gray-700 rounded-l-lg shadow hover:bg-gray-50 dark:hover:bg-gray-600 transition-all duration-300 hidden md:block ${isOpen ? 'right-72' : 'right-0'}`}
                    >
                        {isOpen ? <ChevronRight className="h-3 w-3 text-gray-600 dark:text-gray-300" /> : <ChevronLeft className="h-3 w-3 text-gray-600 dark:text-gray-300" />}
                    </button>
                </>
            }
        </>
    );
};

    
